import axios from 'axios';
import tokenManager from './tokenManager';

// Ensure API base URL ends with /api so routes like /technical-sheets/... resolve to /api/technical-sheets/...
const getApiBaseURL = () => {
  const url = process.env.REACT_APP_API_URL;
  if (!url) return '/api';
  return url.endsWith('/api') ? url : `${url.replace(/\/?$/, '')}/api`;
};

// Create axios instance
const apiClient = axios.create({
  baseURL: getApiBaseURL(),
  timeout: 30000,
});

// Function to get fresh token with proactive refresh
export const getAuthToken = async (instance, accounts) => {
  try {
    return await tokenManager.getToken(instance, accounts);
  } catch (error) {
    console.error('Error acquiring token:', error);
    throw error;
  }
};

// Function to make authenticated API calls
export const authenticatedApiCall = async (instance, accounts, method, url, data = null, config = {}) => {
  let hasRetried = false;

  const makeRequest = async () => {
    const token = await getAuthToken(instance, accounts);
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Prepare headers, ensuring Authorization comes last to avoid overwriting
    const headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`
    };

    // For FormData, remove Content-Type to let browser set boundary automatically
    if (data instanceof FormData) {
      delete headers['Content-Type'];
    }

    // Extract headers from config to prevent override
    const { headers: configHeaders, ...restConfig } = config;
    
    const finalConfig = {
      method,
      url,
      data,
      ...restConfig,
      headers  // Headers come last to ensure they're not overridden
    };

    return await apiClient(finalConfig);
  };

  try {
    return await makeRequest();
  } catch (error) {
    // Check if backend indicated a token refresh is required
    const needsRefresh = error.response?.status === 401 && 
                        (error.response?.data?.refresh_required || 
                         error.response?.data?.code === 'TOKEN_EXPIRED' ||
                         error.response?.data?.code === 'INVALID_TOKEN');
    
    if (needsRefresh && !hasRetried) {
      console.log(`Token expired (${error.response?.data?.code}), delegating to tokenManager...`);
      hasRetried = true;
      
      // Use centralized 401 handling
      const shouldRetry = await tokenManager.handle401Error(instance, accounts);
      
      if (shouldRetry) {
        console.log('Token refreshed successfully, retrying request...');
        // Small delay to ensure token is ready
        await new Promise(resolve => setTimeout(resolve, 100));
        return await makeRequest();
      } else {
        // Token manager handled it (either forced relogin or gave up)
        throw new Error('Authentication failed - please log in again');
      }
    }
    
    // Either not a 401 error, or we've already retried
    throw error;
  }
};

/**
 * Make an authenticated API call with automatic retry on lock conflicts (409)
 * 
 * @param {Object} instance - MSAL instance
 * @param {Array} accounts - MSAL accounts
 * @param {string} method - HTTP method
 * @param {string} url - API endpoint URL
 * @param {Object|null} data - Request data
 * @param {Object} config - Additional axios config
 * @param {Object} retryOptions - Retry configuration
 * @param {number} retryOptions.maxRetries - Maximum retry attempts (default: 3)
 * @param {number} retryOptions.baseDelayMs - Base delay in milliseconds (default: 500)
 * @param {number} retryOptions.maxDelayMs - Maximum delay cap (default: 3000)
 * @param {Function} retryOptions.onRetry - Callback on each retry (attempt, error, delay)
 * @returns {Promise} - Response with additional metadata { data, retried, attempts }
 */
export const authenticatedApiCallWithRetry = async (
  instance, 
  accounts, 
  method, 
  url, 
  data = null, 
  config = {},
  retryOptions = {}
) => {
  const {
    maxRetries = 3,
    baseDelayMs = 500,
    maxDelayMs = 3000,
    onRetry = null
  } = retryOptions;

  let lastError = null;

  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
    
    try {
      const response = await authenticatedApiCall(instance, accounts, method, url, data, config);
      return {
        ...response,
        _retryMetadata: {
          retried: attempt > 1,
          attempts: attempt
        }
      };
    } catch (error) {
      lastError = error;
      
      // Check if this is a retryable lock conflict (409 with ROW_LOCKED)
      const isLockError = error.response?.status === 409 && 
                         error.response?.data?.errorCode === 'ROW_LOCKED';
      
      if (!isLockError || attempt > maxRetries) {
        // Not retryable or retries exhausted
        error._retryMetadata = {
          retried: attempt > 1,
          attempts: attempt,
          retriesExhausted: isLockError && attempt > maxRetries
        };
        throw error;
      }

      // Calculate exponential backoff with jitter
      const exponentialDelay = baseDelayMs * Math.pow(2, attempt - 1);
      const jitter = Math.random() * 200; // 0-200ms jitter
      const delay = Math.min(exponentialDelay + jitter, maxDelayMs);

      // Use server-suggested retry delay if available
      const serverRetryAfter = error.response?.data?.retryAfter;
      const actualDelay = serverRetryAfter ? Math.max(delay, serverRetryAfter) : delay;

      console.log(`[API Retry] Lock conflict on ${method} ${url}, attempt ${attempt}/${maxRetries + 1}, retrying in ${Math.round(actualDelay)}ms`);

      // Call onRetry callback if provided
      if (onRetry) {
        try {
          await onRetry(attempt, error, actualDelay);
        } catch (callbackError) {
          console.warn('[API Retry] onRetry callback error:', callbackError.message);
        }
      }

      await sleep(actualDelay);
    }
  }

  // Should not reach here, but just in case
  throw lastError;
};

/**
 * Check if an error is a lock conflict that could be retried
 * 
 * @param {Error} error - The error to check
 * @returns {boolean} - True if the error is a retryable lock conflict
 */
export const isRetryableLockError = (error) => {
  return error?.response?.status === 409 && 
         error?.response?.data?.errorCode === 'ROW_LOCKED';
};

export default apiClient;
